/**
 * 消息处理工具函数
 * 用于简化消息路由和 ID 管理
 */

import { Message, Conversation, TreeNode, MessageBlock } from '@/types';

// ============================================================================
// 🔥 新增：统一 ID 辅助函数
// ============================================================================

/**
 * 判断是否为待确认 ID（前端生成的非纯数字字符串）
 * - 数字或纯数字字符串 → false（后端确认的ID）
 * - 其他字符串 → true（前端生成的待确认ID）
 */
export function isPendingId(id: string | number | undefined): boolean {
  if (id === undefined) return false;
  if (typeof id === 'number') return false;
  // 纯数字字符串视为已确认ID
  return !/^\d+$/.test(id);
}

/**
 * 判断是否为已确认 ID（后端返回的数字或纯数字字符串）
 * - 数字或纯数字字符串 → true（后端确认的ID）
 * - 其他 → false
 */
export function isConfirmedId(id: string | number | undefined): boolean {
  if (id === undefined) return false;
  if (typeof id === 'number') return true;
  // 纯数字字符串也视为已确认ID
  return /^\d+$/.test(id);
}

/**
 * 生成待确认消息 ID
 * 格式：pending-{timestamp}-{random}
 */
export function createPendingId(): string {
  return `pending-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * 比较两个消息 ID 是否相等
 * 支持 string 和 number 类型的比较
 */
export function isMessageIdEqual(
  id1: string | number | undefined,
  id2: string | number | undefined
): boolean {
  if (id1 === undefined || id2 === undefined) return false;
  // 同类型直接比较
  if (typeof id1 === typeof id2) return id1 === id2;
  // 不同类型转为字符串比较
  return String(id1) === String(id2);
}

/**
 * 生成会话+消息的复合键（用于多会话并发场景）
 */
export function getConversationMessageKey(
  conversationId: string,
  messageId: string | number
): string {
  return `${conversationId}:${messageId}`;
}

// ============================================================================
// 🔥 Phase 3 完成：messageIdMappings 系统已移除
// 消息现在直接通过 message.id (pendingId) 匹配，不再需要额外的映射表
// ============================================================================

/**
 * 在 conversations 中查找包含指定临时消息ID的对话
 * 直接在消息中通过 message.id 查找，无需映射表
 */
export function findConversationByFrontendTempId(
  conversations: Conversation[],
  pendingId: string
): Conversation | undefined {
  // 1. 在消息中查找（使用 message.id 匹配临时消息ID）
  for (const conv of conversations) {
    const messages = conv.messages || [];
    if (messages.some(m => String(m.id) === pendingId)) {
      return conv;
    }
  }

  // 2. 在 pendingFrontendTempIds 中查找（兼容旧数据）
  for (const conv of conversations) {
    if ((conv as any).pendingFrontendTempIds?.includes(pendingId)) {
      return conv;
    }
  }

  return undefined;
}

/**
 * 确定消息应该路由到的目标对话 ID
 */
export function resolveTargetConversationId(
  message: Message,
  conversations: Conversation[]
): string | undefined {
  const messageConversationId = (message as any).conversationId;

  // 1. 优先使用消息中的 conversationId
  if (messageConversationId) {
    return messageConversationId;
  }

  // 2. 通过临时消息ID查找（使用统一的 message.id）
  if (isPendingId(message.id)) {
    const conv = findConversationByFrontendTempId(
      conversations,
      String(message.id)
    );
    if (conv) {
      return conv.id;
    }
  }

  // 🔥 Phase 3 完成：移除了通过后端 ID 查找的逻辑
  // 消息现在直接通过 message.id (pendingId) 匹配

  return undefined;
}

/**
 * 在 treeData 中递归查找节点
 */
export function findNodeInTree(
  nodes: TreeNode[],
  predicate: (node: TreeNode) => boolean
): TreeNode | undefined {
  for (const node of nodes) {
    if (predicate(node)) {
      return node;
    }
    if (node.children && node.children.length > 0) {
      const found = findNodeInTree(node.children, predicate);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * 在 treeData 中递归更新节点
 */
export function updateNodeInTree(
  nodes: TreeNode[],
  predicate: (node: TreeNode) => boolean,
  updater: (node: TreeNode) => TreeNode
): TreeNode[] {
  return nodes.map(node => {
    if (predicate(node)) {
      return updater(node);
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: updateNodeInTree(node.children, predicate, updater),
      };
    }
    return node;
  });
}

/**
 * 查找 treeData 中最深的叶子节点
 */
export function findDeepestLeafNode(nodes: TreeNode[]): TreeNode | null {
  if (!nodes || nodes.length === 0) return null;
  const lastNode = nodes[nodes.length - 1];
  if (lastNode.children && lastNode.children.length > 0) {
    return findDeepestLeafNode(lastNode.children) || lastNode;
  }
  return lastNode;
}

/**
 * 将 MessageBlock 数组转换为 TreeNode 的 contents 格式
 */
export function blocksToContents(blocks: MessageBlock[]): any[] {
  return blocks.map(block => ({
    type: block.type,
    content: block.content,
    createdAt: new Date().toISOString(),
  }));
}

/**
 * 创建 user 节点
 */
export function createUserNode(params: {
  id: number | string;
  blocks: MessageBlock[];
  previousMessageId?: number | null;
  pendingId?: string;
  backendId?: number;
  user?: { id: number; name: string; email: string };
}): TreeNode {
  return {
    id:
      typeof params.id === 'string' ? parseInt(params.id, 10) || 0 : params.id,
    role: 'user',
    contents: blocksToContents(params.blocks),
    previousMessageId: params.previousMessageId,
    isStreaming: false,
    children: [],
    user: params.user,
  } as TreeNode & {
    pendingId?: string;
    backendId?: number;
    messageStatus?: string;
  };
}

/**
 * 创建 assistant 节点
 */
export function createAssistantNode(params: {
  id: number | string;
  blocks?: MessageBlock[];
  previousMessageId?: number | null;
  pendingId?: string;
  backendId?: number;
  isStreaming?: boolean;
  client?: { id: string; name: string; logo?: string };
}): TreeNode {
  return {
    id:
      typeof params.id === 'string' ? parseInt(params.id, 10) || 0 : params.id,
    role: 'assistant',
    contents: params.blocks ? blocksToContents(params.blocks) : [],
    previousMessageId: params.previousMessageId,
    isStreaming: params.isStreaming ?? true,
    client: params.client,
    children: [],
  } as TreeNode & {
    pendingId?: string;
    backendId?: number;
    messageStatus?: string;
  };
}

/**
 * 检查消息是否只是状态更新
 */
export function isStatusOnlyUpdate(message: Message): boolean {
  return !!(message as any)._isStatusOnlyUpdate;
}

/**
 * 检查消息是否包含用户信息（用于同时创建 user 和 assistant 节点）
 */
export function hasUserInfo(message: Message): boolean {
  return !!(message as any)._userInfo;
}

/**
 * 获取消息中的用户信息
 */
export function getUserInfo(message: Message):
  | {
      blocks: MessageBlock[];
      backendId?: number;
      fileIds?: string[];
    }
  | undefined {
  return (message as any)._userInfo;
}
